import { RawScoreDataPoint } from '../../models/score';
import { TimeChartConfig, TimeChartData } from '../Viz/TimeChart/types';
import { formatDate } from '../UserDashboard/utils';
import { Metrics } from './types';
import { DiagnosisCategory, UserDiagnosis } from '../../api/types';

// Flat set of resolved hex strings the chart-config generators read.
// Callers fetch them from Chakra's `useToken` and pass them in.
export type ChartColors = {
  secondary200: string;
  secondary500: string;
  primary500: string;
  orange500: string;
};

const DEFAULT_DOMAIN_PADDING_VALUE = 20;

export const generateTimeChartDataIfMemberView = (
  dataPoints: RawScoreDataPoint[]
): TimeChartData => {
  return dataPoints.map(({ ayble_score, score_date, stress_score }, index) => {
    const normalizedScore = (ayble_score ?? 0) * 10;
    const roundedScore = Math.round(normalizedScore * 100) / 100;

    return {
      day: index + 1,
      formattedDate: formatDate(score_date),
      [Metrics.score]: roundedScore,
      [Metrics.stress]: stress_score ?? 0,
    };
  });
};

export const generateTimeChartConfigIfMemberView = (
  colors: ChartColors,
  visibilityMap: Record<string, boolean>
): TimeChartConfig => {
  return {
    xAxis: {
      dataKey: 'day',
    },
    yAxis: {
      domain: [0, 10],
      lines: [
        {
          color: colors.secondary500,
          dataKey: Metrics.score,
          displayLabel: 'Overall',
          visible: visibilityMap[Metrics.score],
        },
        {
          color: colors.primary500,
          dataKey: Metrics.stress,
          displayLabel: 'Stress',
          visible: visibilityMap[Metrics.stress],
        },
      ],
    },
  };
};

export const generateTimeChartDataIfNotMemberView = (
  dataPoints: RawScoreDataPoint[]
): TimeChartData => {
  return dataPoints.map(
    ({ cd_score, ibs_score, score_date, uc_score, upper_gi_score }, index) => {
      return {
        day: index + 1,
        formattedDate: formatDate(score_date),
        [Metrics.cdScore]: cd_score ?? 0,
        [Metrics.ibsScore]: ibs_score ?? 0,
        [Metrics.ucScore]: uc_score ?? 0,
        [Metrics.upperGIScore]: upper_gi_score ?? 0,
      };
    }
  );
};

export const computeNotMemberViewMetrics = (
  colors: ChartColors,
  visibilityMap: Record<string, boolean>,
  userDiagnosis: UserDiagnosis[]
) => {
  const hasUpperGI = userDiagnosis.find(
    (diagnosis) => diagnosis.category === DiagnosisCategory.UpperGI
  );
  const hasUlcerativeColitis = userDiagnosis.find(
    (diagnosis) => diagnosis.diagnosis === 'Ulcerative Colitis'
  );
  const hasCrohnDisease = userDiagnosis.find(
    (diagnosis) => diagnosis.diagnosis === `Crohn's Disease`
  );

  const metrics = [
    {
      color: colors.secondary200,
      dataKey: Metrics.ibsScore,
      displayLabel: 'IBS-SSS',
      visible: visibilityMap[Metrics.ibsScore],
    },
  ];

  if (hasUpperGI) {
    metrics.push({
      color: colors.primary500,
      dataKey: Metrics.upperGIScore,
      displayLabel: 'PAGI-SYM',
      visible: visibilityMap[Metrics.upperGIScore],
    });
  }

  if (hasUlcerativeColitis) {
    metrics.push({
      color: '#5D1505',
      dataKey: Metrics.ucScore,
      displayLabel: 'PSCCAI',
      visible: visibilityMap[Metrics.ucScore],
    });
  }

  if (hasCrohnDisease) {
    metrics.push({
      color: colors.orange500,
      dataKey: Metrics.cdScore,
      displayLabel: 'mHI-CD',
      visible: visibilityMap[Metrics.cdScore],
    });
  }

  return metrics;
};

// When not in member view, only one metric is active so we need to
// compute the domain of the chart based on that.
export const generateTimeChartConfigIfNotMemberView = (
  colors: ChartColors,
  visibilityMap: Record<string, boolean>,
  timeChartData: TimeChartData,
  userDiagnosis: UserDiagnosis[]
): TimeChartConfig => {
  const activeMetric = Object.keys(visibilityMap).find((k) => visibilityMap[k]);
  if (!activeMetric) throw new Error('At least one metric should be active');

  const activeMetricData = timeChartData.map(
    (dataPoint) => +dataPoint[activeMetric]
  );
  const minDomain = Math.min(...activeMetricData) ?? 0;
  // We add a bit to make room in the chart for the highest points
  const maxDomain = Math.max(...activeMetricData) ?? 0;

  const padding =
    maxDomain > 10 ? DEFAULT_DOMAIN_PADDING_VALUE : (maxDomain / 100) * 10;

  const maxDomainWithPadding =
    maxDomain > 0 ? maxDomain + padding : DEFAULT_DOMAIN_PADDING_VALUE;

  return {
    xAxis: {
      dataKey: 'day',
    },
    yAxis: {
      domain: [minDomain, maxDomainWithPadding],
      lines: computeNotMemberViewMetrics(colors, visibilityMap, userDiagnosis),
    },
  };
};
